import React, { useEffect, useState, useMemo, useCallback } from "react";
import CurrencyFormat from "react-currency-format";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { GetManualJournalAtom } from "../../store/manualJounalAtom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ManualJournalFilter from "./ManualJournalFilter";
import StatusBadge from "../../generals/StatusBadge";

// Memoized individual journal item component
const JournalItem = React.memo(({ 
  journal, 
  isSelected, 
  onItemClick, 
  currModuleId, 
  currModuleName, 
  currUserMenu 
}) => {
  // Memoize expensive calculations per item
  const formattedDate = useMemo(() => 
    moment(journal?.Transaction_Date).format("DD-MM-YYYY"), 
    [journal?.Transaction_Date]
  );
  
  const formattedAmount = useMemo(() =>
    parseFloat(journal?.Amount || 0).toFixed(2),
    [journal?.Amount]
  );

  const handleClick = useCallback(() => {
    onItemClick(journal);
  }, [journal, onItemClick]);

  return (
    <div
      onClick={handleClick}
      className={`p-2 lg:p-1 grid grid-cols-2 lg:grid-cols-none border-b-[1px] cursor-pointer border-gray-200 ${
        isSelected ? "bg-gray-200" : "hover:bg-gray-100"
      }`}
    >
      <div className="flex flex-row">
        <div className="ml-1">
          <div className="text-sm font-bold text-gray-700">
            {journal?.Credit_Note_Number}
          </div>
          <div className="text-sm lg:text-xs text-gray-600">
            {journal?.journal_type_name}
          </div>
          <div className="text-sm lg:text-xs text-gray-600">
            {formattedDate}
          </div>
        </div>
      </div>
      <div className="lg:mt-2 lg:ml-5">
        <div className={twMerge("text-sm font-normal")}>
          <StatusBadge
            statusId={journal?.status_id ?? journal?.Status_Id}
            statusName={journal?.status_name ?? journal?.Status_Name}
          />
        </div>
        <div className="mt-[2px] text-sm lg:text-xs font-bold text-gray-700">
          <CurrencyFormat
            value={formattedAmount}
            displayType={"text"}
            thousandSeparator={true}
            prefix={"₦"}
          />
        </div>
      </div>
    </div>
  );
});

JournalItem.displayName = 'JournalItem';

// Virtual scrolling hook for large datasets
const useVirtualScrolling = (items, itemHeight = 80, containerHeight = 700) => {
  const [scrollTop, setScrollTop] = useState(0);
  
  const visibleItems = useMemo(() => {
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 2,
      items.length
    );
    
    return {
      startIndex: Math.max(0, startIndex),
      endIndex,
      items: items.slice(Math.max(0, startIndex), endIndex)
    };
  }, [items, scrollTop, itemHeight, containerHeight]);

  const totalHeight = items.length * itemHeight;
  const offsetY = visibleItems.startIndex * itemHeight;

  return {
    visibleItems: visibleItems.items,
    totalHeight,
    offsetY,
    setScrollTop
  };
};

function ManualJournalScroll() {
  const journalAtom = GetManualJournalAtom();
  const [tableData, setTableData] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  
  // Memoize URL parsing to prevent recalculation on every render
  const urlParts = useMemo(() => {
    const url = pathname.split("/");
    return {
      currModuleId: url[1],
      currModuleName: url[2],
      currUserMenu: url[3]
    };
  }, [pathname]);

  // Memoize filtered data to prevent unnecessary recalculations
  const processedData = useMemo(() => {
    return journalAtom?.account_info || [];
  }, [journalAtom?.account_info]);

  // Use virtual scrolling for large datasets
  const { visibleItems, totalHeight, offsetY, setScrollTop } = useVirtualScrolling(
    tableData, 
    80, // estimated item height
    700 // container height
  );

  // Update table data only when processed data actually changes
  useEffect(() => {
    // Deep comparison to avoid unnecessary updates
    const newDataString = JSON.stringify(processedData);
    const currentDataString = JSON.stringify(tableData);
    
    if (newDataString !== currentDataString) {
      setTableData(processedData);
    }
  }, [processedData]); // Remove tableData from deps to avoid infinite loops

  // Memoize filter callback
  const onFilterSelect = useCallback((selection) => {
    if (selection === 0) {
      setTableData(journalAtom?.account_info || []);
      return;
    }
    
    const filteredList = (journalAtom?.account_info || []).filter(
      item => item?.Status_Id === selection
    );
    setTableData(filteredList);
  }, [journalAtom?.account_info]);

  // Memoize navigation callback
  const viewItemDetails = useCallback((item) => {
    const { currModuleId, currModuleName, currUserMenu } = urlParts;
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${item.account_ref_no}`);
  }, [navigate, urlParts]);

  // Handle scroll for virtual scrolling
  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, [setScrollTop]);

  // Memoize the rendered items to prevent unnecessary re-renders
  const renderedItems = useMemo(() => {
    return visibleItems.map((journal, index) => (
      <JournalItem
        key={journal?.account_ref_no || `journal-${index}`}
        journal={journal}
        isSelected={id === journal?.account_ref_no}
        onItemClick={viewItemDetails}
        {...urlParts}
      />
    ));
  }, [visibleItems, id, viewItemDetails, urlParts]);

  return (
    <section className="border-[1px] rounded-lg bg-white border-gray-200 flex flex-col h-[300px] lg:h-[700px] scrollbar-thumb-gray-500 scrollbar-thin scrollbar-track-gray-100">
      <div className="border-b-[1px] border-b-gray-300 flex-shrink-0">
    
      </div>
      
      <div 
        className="flex-1 overflow-y-auto"
        onScroll={handleScroll}
        style={{ position: 'relative' }}
      >
        {/* Virtual scrolling container */}
        <div style={{ height: totalHeight, position: 'relative' }}>
          <div 
            style={{ 
              transform: `translateY(${offsetY}px)`,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0
            }}
          >
            {renderedItems}
          </div>
        </div>
        
        {/* Fallback for small datasets - render normally */}
        {tableData.length < 50 && (
          <div>
            {tableData.map((journal, i) => (
              <JournalItem
                key={journal?.account_ref_no || `journal-${i}`}
                journal={journal}
                isSelected={id === journal?.account_ref_no}
                onItemClick={viewItemDetails}
                {...urlParts}
              />
            ))}
          </div>
        )}
        
        {tableData.length === 0 && (
          <div className="flex justify-center items-center h-32 text-gray-500">
            No journals found
          </div>
        )}
      </div>
    </section>
  );
}

export default React.memo(ManualJournalScroll);